import { useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Button } from "../../../app/components/ui/button";
import { Input } from "../../../app/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../app/components/ui/form";
import { Separator } from "../../../app/components/ui/separator";
import { ProductSelector } from "../../../components/selectors/ProductSelector";
import { DealerPickerPanel } from "../../dealers/components/DealerPickerPanel";
import { TRACKING_EVENTS } from "../../../constants/trackingEvents";
import { api } from "../../../lib/api";
import { applyLaravelValidationErrors, getErrorMessage, getLaravelValidationErrors } from "../../../lib/laravelErrors";
import { trackEvent } from "../../../lib/tracking";
import { trackFirstInventoryAddedOnce } from "../../../lib/trackingMilestones";
import { useAuth } from "../../../providers/AuthProvider";

export type InventoryEntryFormValues = {
  dealer_id: string;
  reference_no: string;
  items: Array<{
    product_id: string;
    quantity: string;
    purchase_price: string;
    selling_price: string;
    mrp: string;
  }>;
};

const emptyItem = (): InventoryEntryFormValues["items"][number] => ({
  product_id: "",
  quantity: "1",
  purchase_price: "0",
  selling_price: "0",
  mrp: "0",
});

/** Sum of (purchase_price × quantity) for each line; two decimal places for the API. */
function computeInventoryEntryTotal(
  items: InventoryEntryFormValues["items"],
): string {
  let sum = 0;
  for (const it of items) {
    const qty = Number.parseFloat(String(it.quantity ?? "").trim()) || 0;
    const price = Number.parseFloat(String(it.purchase_price ?? "").trim()) || 0;
    sum += qty * price;
  }
  return sum.toFixed(2);
}

export function InventoryEntryForm({ onSuccess }: { onSuccess: () => void }) {
  const { token } = useAuth();
  const [rootError, setRootError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<InventoryEntryFormValues>({
    defaultValues: {
      dealer_id: "",
      reference_no: "",
      items: [emptyItem()],
    },
  });

  const items = useFieldArray({ control: form.control, name: "items" });

  const watchedDealer = form.watch("dealer_id");
  const watchedRef = form.watch("reference_no");
  const watchedItems = form.watch("items");
  const computedTotalAmount = useMemo(
    () => computeInventoryEntryTotal(Array.isArray(watchedItems) ? watchedItems : []),
    [watchedItems],
  );
  const canSubmit = Boolean(
    watchedDealer &&
      watchedRef?.trim() &&
      Array.isArray(watchedItems) &&
      watchedItems.length > 0,
  );

  async function onSubmit(values: InventoryEntryFormValues) {
    if (!token) return;
    setRootError(null);
    setSubmitting(true);
    try {
      await api.inventory.create({
        token,
        payload: {
          dealer_id: values.dealer_id,
          reference_no: values.reference_no.trim(),
          total_amount: computeInventoryEntryTotal(values.items),
          items: values.items.map((it) => ({
            product_id: it.product_id,
            quantity: it.quantity,
            purchase_price: it.purchase_price,
            selling_price: it.selling_price,
            mrp: it.mrp,
          })),
        },
      });
      trackEvent(TRACKING_EVENTS.INVENTORY_ADDED, { items: values.items.length });
      trackFirstInventoryAddedOnce();
      onSuccess();
    } catch (e) {
      const errors = getLaravelValidationErrors(e);
      if (errors) {
        applyLaravelValidationErrors(form.setError, errors);
        setRootError("Please fix the highlighted fields.");
        return;
      }
      setRootError(getErrorMessage(e, "Failed to create inventory entry"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {rootError ? (
        <Alert variant="destructive">
          <AlertTitle>Save failed</AlertTitle>
          <AlertDescription>{rootError}</AlertDescription>
        </Alert>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="dealer_id"
              rules={{ required: "Dealer is required" }}
              render={({ field }) => (
                <FormItem className="md:col-span-3">
                  <FormLabel>Dealer</FormLabel>
                  <FormControl>
                    <DealerPickerPanel
                      value={field.value}
                      onChange={field.onChange}
                      items={(watchedItems ?? []).map((it) => ({ product_id: it.product_id, quantity: it.quantity }))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reference_no"
              rules={{ required: "Reference no is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reference no</FormLabel>
                  <FormControl>
                    <Input placeholder="INV-0001" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Total amount</FormLabel>
              <FormControl>
                <Input
                  readOnly
                  tabIndex={-1}
                  className="bg-muted"
                  type="text"
                  inputMode="decimal"
                  value={computedTotalAmount}
                  aria-readonly="true"
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                Calculated as the sum of quantity × purchase price for each row.
              </p>
            </FormItem>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">Items</div>
                <div className="text-sm text-muted-foreground">Add one or more products</div>
              </div>
              <Button type="button" variant="outline" onClick={() => items.append(emptyItem())}>
                Add row
              </Button>
            </div>

            <Separator />

            <div className="space-y-4">
              {items.fields.map((f, idx) => (
                <div key={f.id} className="rounded-md border p-4 space-y-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">Row {idx + 1}</div>
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      disabled={items.fields.length <= 1}
                      onClick={() => items.remove(idx)}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name={`items.${idx}.product_id`}
                      rules={{ required: "Product is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Product</FormLabel>
                          <FormControl>
                            <ProductSelector value={field.value} onValueChange={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`items.${idx}.quantity`}
                      rules={{ required: "Quantity is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Quantity</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <FormField
                      control={form.control}
                      name={`items.${idx}.purchase_price`}
                      rules={{ required: "Purchase price is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Purchase price</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`items.${idx}.selling_price`}
                      rules={{ required: "Selling price is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Selling price</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name={`items.${idx}.mrp`}
                      rules={{ required: "MRP is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>MRP</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button type="submit" disabled={!canSubmit || submitting}>
              {submitting ? "Saving..." : "Create entry"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

